import { FC, Fragment, createRef, memo, useContext, useEffect, useMemo, useState } from 'react';
import { HotelsMapProps } from 'ts/hotel/interfaces/Map';
import { MapContainer, Marker, Polygon, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { OSMInstructions, Position } from 'ts/hotel/interfaces/Generic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faThumbTack } from '@fortawesome/free-solid-svg-icons';
import { MapHotelContext } from 'contexts/MapHotelContext';
import Rating from 'components/ui/Rating/Rating';
import { getCenteredHotel, formatPriceHotel } from 'utils/hotel';
import AdditionalMarkerOSM from './AdditionalMarkerOSM';
import Leaflet from 'leaflet';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { imgWaypointA, imgWaypointB, imgRedPoint, imgBluePoint, imgCenterPoint } from 'constants/generic';

const HotelsOSM: FC<HotelsMapProps> = ({ ...props }) => {
  
  const { focusedHotel, setCentered, setFocusedHotel, initedMap, currency, geoPolygons } =
    useContext(MapHotelContext);
  const { neighborhoodList } = useContext(HotelFilterContext);
  const [polRouteWal, setPolRouteWal] = useState<[number, number][]>([]);
  const [polRouteDr, setPolRouteDr] = useState<[number, number][]>([]);
  const [texts, setTexts] = useState<{
    driving: OSMInstructions[] | null;
    walking: OSMInstructions[] | null;
  }>({ driving: null, walking: null });
  const { t } = useTranslation();

  const iconRed = new Leaflet.Icon({
    iconUrl: imgRedPoint,
    iconRetinaUrl: imgRedPoint,
    popupAnchor: [-0, -0],
    iconSize: [16, 16],
  });

  const iconBlue = new Leaflet.Icon({
    iconUrl: imgBluePoint,
    iconSize: [16, 16],
  });

  const iconA = new Leaflet.Icon({
    iconUrl: imgWaypointA,
    iconSize: [16, 28],
    className: 'map-osm-marker',
  });

  const iconB = new Leaflet.Icon({
    iconUrl: imgWaypointB,
    iconSize: [16, 28],
    className: 'map-osm-marker',
  });

  const iconCenter = new Leaflet.Icon({
    iconUrl: imgCenterPoint,
    iconSize: [16, 28],
  });

  const fetchRoute = () => {
    (async () => {
      try {
        const urlWal = `https://routing.openstreetmap.de/routed-foot/route/v1/driving/${props.from?.lng},${props.from?.lat};${props.to?.lng},${props.to?.lat}?steps=true&overview=false`;

        const responseWal = await fetch(urlWal);
        const dataWal = await responseWal.json();
        if (dataWal.routes?.[0].legs?.[0].steps) {
          let newPol: any = [];
          let newTextsWal: OSMInstructions[] = [];

          dataWal.routes[0].legs[0].steps.forEach((step: any) => {
            newTextsWal.push({
              modifier: step.maneuver.modifier
                ? 'osm.' + step.maneuver.modifier?.replaceAll(' ', '_')
                : '',
              type: step.maneuver.type ? 'osm.' + step.maneuver.type.replaceAll(' ', '_') : '',
              distance: step.distance,
              name: step.name,
            });
            step.intersections.forEach((inter: any) => {
              newPol.push([inter.location[1], inter.location[0]]);
            });
          });

          setTexts({ ...texts, walking: newTextsWal });
          const steps = dataWal.routes[0].legs?.[0]?.steps || [];
          props.setDirections((prev: any) => ({
            ...prev,
            walking: {
              instructions: newTextsWal,
              summary: {
                totalDistance: dataWal.routes[0].distance,
                totalTime: dataWal.routes[0].duration,
              },
              from: {
                address: steps[0].name,
                lat: steps[0].maneuver.location[1],
                lng: steps[0].maneuver.location[0],
              },
              to: {
                address: steps[steps.length - 1].name,
                lat: steps[steps.length - 1].maneuver.location[1],
                lng: steps[steps.length - 1].maneuver.location[0],
              },
            },
          }));
          setPolRouteWal(newPol);
        }
      } catch (err) {}
    })();

    (async () => {
      try {
        const urlDr = `https://router.project-osrm.org/route/v1/foot/${props.from?.lng},${props.from?.lat};${props.to?.lng},${props.to?.lat}?steps=true&overview=false`;
        const responseDr = await fetch(urlDr);
        const dataDr = await responseDr.json();
        if (dataDr.routes?.[0].legs?.[0].steps) {
          let newPol: any = [];
          let newTextsDr: OSMInstructions[] = [];

          dataDr.routes[0].legs[0].steps.forEach((step: any) => {
            newTextsDr.push({
              modifier: step.maneuver.modifier
                ? 'osm.' + step.maneuver.modifier?.replaceAll(' ', '_')
                : '',
              type: step.maneuver.type ? 'osm.' + step.maneuver.type.replaceAll(' ', '_') : '',
              distance: step.distance,
              name: step.name,
            });
            step.intersections.forEach((inter: any) => {
              newPol.push([inter.location[1], inter.location[0]]);
            });
          });

          const steps = dataDr.routes[0].legs?.[0]?.steps || [];
          setTexts({ ...texts, driving: newTextsDr });
          props.setDirections((prev: any) => ({
            ...prev,
            driving: {
              instructions: newTextsDr,
              summary: {
                totalDistance: dataDr.routes[0].distance,
                totalTime: dataDr.routes[0].duration,
              },
              from: {
                address: steps[0].name,
                lat: steps[0].maneuver.location[1],
                lng: steps[0].maneuver.location[0],
              },
              to: {
                address: steps[steps.length - 1].name,
                lat: steps[steps.length - 1].maneuver.location[1],
                lng: steps[steps.length - 1].maneuver.location[0],
              },
            },
          }));
          setPolRouteDr(newPol);
        }
      } catch (err) {}
    })();
  };

  useEffect(() => {
    if (props.to && props.from) {
      setPolRouteDr([]);
      setPolRouteWal([]);
      fetchRoute();
    }
  }, [props.to, props.from]); // eslint-disable-line

  useEffect(() => {
    closeAllPopups();
    setPolRouteDr([]);
    setPolRouteWal([]);
  }, [props.clearDate]); // eslint-disable-line

  useEffect(() => {
    if (focusedHotel?.htuKey && focusedHotel?.open) {
      refsById[focusedHotel.htuKey]?.current?.openPopup();
    }
  }, [focusedHotel]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeAllPopups = () => {
    props.hotels?.forEach((hotel) => {
      refsById[hotel.htuKey]?.current?.closePopup();
    });
  };

  const refsById = useMemo(() => {
    let refs = {} as unknown as { [key: string]: any };
    props.hotels?.forEach((hotel) => {
      refs[hotel.htuKey] = createRef();
    });
    return refs;
  }, [props.hotels]);

  const onClickFrom = (position: Position) => {
    props.setFrom(position);
    closeAllPopups();
  };

  const onClickTo = (position: Position) => {
    props.setTo(position);
    closeAllPopups();
  };
  return (
    <Fragment>
      {props.center && initedMap && (
        <MapContainer>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {props.hotels?.map((hotel) => (
            <Marker
              ref={refsById[hotel.htuKey]}
              key={hotel.htuKey}
              zIndexOffset={8}
              position={{ lat: hotel.position?.latitude || 0, lng: hotel.position?.longitude || 0 }}
              icon={iconBlue}
            >
              <Popup>
                <div className="map-marker map-marker-osm">
                  <div className="text-bold">{hotel.item?.name}</div>
                  <Rating value={hotel.item?.star || 0} className="mb-5" />
                  <div className="mb-5">{hotel.addressInfo?.address}</div>
                  {!!hotel.roomClasses?.[0]?.price && (
                    <div className="mb-5 text-bold text-lg">
                      {formatPriceHotel(hotel.roomClasses?.[0]?.price, currency)}
                    </div>
                  )}
                  <div className="marker-actions">
                    <button
                      className="btn btn-default"
                      onClick={() =>
                        onClickFrom({
                          lat: hotel.position?.latitude || 0,
                          lng: hotel.position?.longitude || 0,
                          address: hotel.addressInfo?.address,
                        })
                      }
                    >
                      <img src={imgWaypointA} alt="" /> {t('group.from')}
                    </button>
                    <button
                      className="btn btn-default"
                      onClick={() =>
                        onClickTo({
                          lat: hotel.position?.latitude || 0,
                          lng: hotel.position?.longitude || 0,
                          address: hotel.addressInfo?.address,
                        })
                      }
                    >
                      <img src={imgWaypointB} alt="" />
                      {t('group.to')}
                    </button>
                    <button
                      className="btn btn-default"
                      onClick={() => {
                        closeAllPopups();
                        setCentered(getCenteredHotel(hotel));
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                      {t('hotel.center_me')}
                    </button>
                    <button
                      className="btn btn-default"
                      onClick={() => {
                        closeAllPopups();
                        setFocusedHotel({
                          lat: hotel.position?.latitude || 0,
                          lng: hotel.position?.longitude || 0,
                          htuKey: hotel.htuKey,
                          type: 'hotel',
                          open: false,
                          name: '',
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faThumbTack} />
                      {t('hotel.focus')}
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          <AdditionalMarkerOSM
            value={props.additionalCenter}
            onClickTo={props.setTo}
            onClickFrom={props.setFrom}
            onClickCentered={setCentered}
            icon={iconRed}
            zIndexOffset={10}
          />

          <AdditionalMarkerOSM
            value={props.from}
            onClickTo={props.setTo}
            onClickFrom={props.setFrom}
            onClickCentered={setCentered}
            icon={iconA}
            zIndexOffset={5}
          />

          <AdditionalMarkerOSM
            value={props.to}
            onClickTo={props.setTo}
            onClickFrom={props.setFrom}
            onClickCentered={setCentered}
            icon={iconB}
            zIndexOffset={5}
          />

          {props.showPolygon &&
            geoPolygons?.map((pol, index) => (
              <Polygon key={index} positions={pol} color="#FF0000" fillOpacity={0.1} />
            ))}
          {!!polRouteWal.length && (
            <Polyline positions={polRouteWal} color="#107ab0" opacity={0.7} weight={4} />
          )}
          {!!polRouteDr.length && (
            <Polyline positions={polRouteDr} color="green" opacity={0.7} weight={4} />
          )}

          {neighborhoodList
            ?.filter((nb) => !!nb.active && !!nb.geoPolygon?.length)
            .map((nb) => (
              <Polygon
                positions={nb.geoPolygon || []}
                color="#366e34"
                fillOpacity={0.1}
                key={nb.code}
              />
            ))}

          {props.centerMarker && !isNaN(props.centerMarker.lat) && (
            <Marker zIndexOffset={10} position={props.centerMarker} icon={iconCenter} />
          )}

          <ChangeMapView
            coords={props.additionalCenter ? props.additionalCenter : props.center}
            zoom={props.zoom}
          />
        </MapContainer>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(HotelsOSM, 'HotelsOSM');

interface ChangeMapViewProps {
  coords: Position;
  zoom: number;
}

const ChangeMapViewComponent: FC<ChangeMapViewProps> = ({ coords, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (isNaN(coords.lat)) return;
    map.setView(coords, zoom);
  }, [coords, zoom]); // eslint-disable-line

  return null;
};

const ChangeMapView = memo(ChangeMapViewComponent);
